import { INotesByTagsQuery, INoteTagsQuery } from '@rhizome/pgtypes';
import TippyHeadless from '@tippyjs/react/headless';
import { setWidgets } from 'apps/exegesis/pages/notes/dashboard';
import { colors } from 'apps/exegesis/styles/themes';
import {
  useFetcher,
  usePaginatedQuery,
} from 'apps/exegesis/utils/DataFetchers';
import { useAuthor } from 'apps/exegesis/utils/useAuthor';
import { usePrivacy } from 'apps/exegesis/utils/usePrivacy';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Theme } from '../../styles/themes';
import DateDisplay from '../DateDisplay';
import { ErrorDisplay } from '../StatusUI/ErrorDisplay';
import { LoadingPulser } from '../StatusUI/LoadingPulser';
import { TagMenu } from '../TagDisplay';
import { TagList, Title, TitleText } from './BlocksByTagsWidget';
import { NotesByTagsWidgetProps, WidgetUtilityProps } from './WidgetWrapper';

export const NotesByTagsWidget = (
  props: NotesByTagsWidgetProps & WidgetUtilityProps
) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const privacy = usePrivacy();
  const author = useAuthor((state) => state.author);

  const { data: notes, status: notesStatus } =
    usePaginatedQuery<INotesByTagsQuery>(
      [
        'NotesByTags',
        {
          tags: props.tags,
          public: privacy === 'public' ? true : null,
          creation_date: null,
          author: author || null,
          limit: true,
        },
      ],
      { paginateOn: 'creation_date', existingRef: containerRef }
    );

  const [showSettings, setShowSettings] = useState(false);
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  const closeSettings = () => {
    setShowSettings(false);
  };

  const [searchField, setSearchField] = useState('');

  const handleSearchFieldChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (evt.target !== null) setSearchField(evt.target.value);
  };

  const { data: blockTags, status: tagStatus } = useFetcher<INoteTagsQuery>(
    [
      'NoteTags',
      {
        tags: props.tags,
        public: privacy === 'public',
        author: author || null,
      },
    ],
    { keepPreviousData: true }
  );

  return (
    <Container>
      <Title>
        <TitleText>
          {props.tags.length > 0 ? 'notes tagged ' : 'no tags selected. '}
        </TitleText>
        <TippyHeadless
          interactive={true}
          offset={[0, 0]}
          placement="bottom-start"
          visible={showSettings}
          arrow={false}
          onClickOutside={closeSettings}
          render={(attrs) => (
            <CustomTippy className="box" tabIndex={-1} {...attrs}>
              {blockTags ? (
                <TagMenu
                  tags={blockTags}
                  selectedTags={new Set(props.tags)}
                  setSelectedTags={(newSet: Set<string>) => {
                    let newWidgets = [...props.widgets];
                    (newWidgets[props.index] as NotesByTagsWidgetProps).tags =
                      Array.from(newSet);
                    setWidgets(newWidgets);
                  }}
                  searchField={searchField}
                  setSearchField={setSearchField}
                  handleSearchFieldChange={handleSearchFieldChange}
                />
              ) : (
                <div>loading</div>
              )}
            </CustomTippy>
          )}
        >
          {props.tags.length > 0 ? (
            <TagList onClick={toggleSettings}>
              {props.tags.map((tag, i) => {
                if (i === props.tags.length - 1) {
                  return tag;
                }
                return `${tag}, `;
              })}
            </TagList>
          ) : (
            <TagList onClick={toggleSettings}>{'[select tags]'}</TagList>
          )}
        </TippyHeadless>
      </Title>
      <ScrollContainer ref={containerRef}>
        {notesStatus === 'loading' ? (
          <div style={{ height: '150px' }}>
            <LoadingPulser />
          </div>
        ) : null}
        {notesStatus === 'error' ? <ErrorDisplay /> : null}
        {props.tags.length > 0
          ? notes?.pages?.map((page) => {
              return page.map((note) => {
                return (
                  <SidebarNote
                    public={privacy === 'public'}
                    key={note.note_id}
                    noteId={note.note_id}
                    title={note.title}
                    creationDate={note.creation_date}
                    lastEditedDate={note.last_edited_date}
                  />
                );
              });
            })
          : null}
      </ScrollContainer>
    </Container>
  );
};
const CustomTippy = styled.div`
  width: 250px;
  /* height: 250px; */
  -webkit-box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.15);
  box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.15);
  border: 1px solid ${colors.offBlack};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const ScrollContainer = styled.div`
  overflow-y: auto;
`;

const SidebarNote = (props: {
  noteId: string;
  title: string | null;
  creationDate: Date;
  lastEditedDate: Date;
  public: boolean;
}) => {
  const router = useRouter();
  let noteId = '';
  if (router.query.slug) {
    noteId = router.query.slug[0];
  }
  return (
    <Link
      passHref
      href={
        props.public ? '/post/' + props.noteId : '/notes/editor/' + props.noteId
      }
    >
      <SidebarNoteContainer highlight={noteId === props.noteId}>
        <NoteTitleText>{props.title || 'untitled note'}</NoteTitleText>
        <SubtitleText>
          <DateDisplay
            default="lastEdited"
            created={props.creationDate}
            lastEdited={props.lastEditedDate}
          />
        </SubtitleText>
      </SidebarNoteContainer>
    </Link>
  );
};

const SidebarNoteContainer = styled.div<{ highlight: boolean }>`
  background-color: ${(props: { theme: Theme; highlight: boolean }) =>
    props.highlight ? props.theme.colors.main.medium : 'none'};
  :hover {
    background-color: ${(props: { theme: Theme; highlight: boolean }) =>
      props.highlight
        ? props.theme.colors.main.medium
        : props.theme.colors.main.light};
  }
`;

const NoteTitleText = styled.p`
  margin: 0px;
  font-size: 1rem;
`;
const SubtitleText = styled.div`
  margin: 0px;
  font-style: italic;
  font-size: 0.75rem;
`;
